import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, UserCheck, ArrowUpRight, Sparkles, BookOpenCheck, Layers, Award } from 'lucide-react';

const PORTAILS = [
  {
    role: 'ÉLÈVE',
    kicker: 'Permis B · AAC · CS · B96',
    title: 'Je prépare mon Code et ma conduite',
    desc: "Magazine pédagogique, 38 leçons REMC chartrées, livret d'apprentissage, examens blancs. Comprendre la route, pas juste cocher des cases.",
    bullets: [
      'Cours en voiture · 38 leçons REMC',
      "Livret d'apprentissage synchronisé",
      'Examens blancs · simulations Code & conduite',
    ],
    stats: [
      { v: '38', l: 'Leçons REMC' },
      { v: '100 %', l: 'Gratuit' },
      { v: '0', l: 'QCM bourrage' },
    ],
    icon: GraduationCap,
    href: '/eleve',
    accent: 'from-red-700/30 via-red-900/10 to-transparent',
    badge: 'Parcours élève',
    cta: 'Découvrir mon espace',
  },
  {
    role: 'ENSEIGNANT ECSR',
    kicker: 'Moniteur certifié · suivi de mes élèves',
    title: "J'accompagne mes élèves vers la réussite",
    desc: 'Dashboard pédagogique, suivi de chaque élève en temps réel, validation des compétences REMC, grille officielle d\'évaluation permis B.',
    bullets: [
      'Dashboard enseignant · code prof unique',
      'Validation REMC compétence par compétence',
      "Grille officielle d'évaluation permis B",
    ],
    stats: [
      { v: '∞', l: 'Élèves suivis' },
      { v: '38', l: 'Compétences à valider' },
      { v: '4', l: 'Niveaux C1→C4' },
    ],
    icon: UserCheck,
    href: '/enseignant',
    accent: 'from-amber-700/25 via-orange-900/10 to-transparent',
    badge: 'Espace pro',
    cta: 'Accéder à mon dashboard',
  },
];

function PortailCard({ p, index }) {
  const Icon = p.icon;
  return (
    <Link
      to={p.href}
      data-testid={`portail-pro-card-${p.role.toLowerCase().replace(/\s+/g, '-')}`}
      className="group relative rounded-3xl border border-white/10 bg-[#0d0d0e] overflow-hidden hover:border-red-700/50 transition-all duration-300 flex flex-col"
    >
      {/* Decorative gradient */}
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${p.accent} opacity-70 group-hover:opacity-100 transition-opacity`} />
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/10 blur-3xl group-hover:bg-red-600/20 transition-all" />

      <div className="relative z-10 p-5 sm:p-7 lg:p-9 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-5 sm:mb-7">
          <div>
            <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.28em] text-red-400 font-bold mb-1.5 font-mono-spec">
              0{index + 1} · {p.badge}
            </div>
            <div className="text-[11px] sm:text-[11.5px] uppercase tracking-[0.2em] text-white/50 font-semibold">{p.role}</div>
          </div>
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-red-600/15 border border-red-700/40 flex items-center justify-center text-red-400 group-hover:bg-red-600 group-hover:text-white group-hover:scale-110 transition-all duration-300">
            <Icon size={22} strokeWidth={1.6} />
          </div>
        </div>

        {/* Title */}
        <div className="text-red-400 text-[11px] sm:text-[12px] font-mono-spec uppercase tracking-wider mb-2">{p.kicker}</div>
        <h3 className="font-serif-editorial text-white text-[22px] sm:text-[28px] lg:text-[34px] leading-[1.05] sm:leading-[1.02] tracking-tight">{p.title}</h3>
        <p className="text-white/65 text-[13px] sm:text-[14px] mt-3 sm:mt-4 leading-relaxed">{p.desc}</p>

        {/* Bullets */}
        <ul className="mt-5 sm:mt-6 space-y-2 sm:space-y-2.5">
          {p.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 sm:gap-2.5 text-white/75 text-[12.5px] sm:text-[13.5px]">
              <Sparkles size={13} className="text-red-400 shrink-0 mt-1" />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* Stats strip */}
        <div className="mt-5 sm:mt-7 grid grid-cols-3 gap-px bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          {p.stats.map((s, i) => (
            <div key={i} className="bg-[#0a0a0b] px-2 sm:px-3 py-3 sm:py-3.5 text-center">
              <div className="font-serif-editorial text-red-500 text-[18px] sm:text-[22px] lg:text-[26px] leading-none">{s.v}</div>
              <div className="text-white/55 text-[9px] sm:text-[9.5px] mt-1 sm:mt-1.5 font-mono-spec uppercase tracking-[0.1em]">{s.l}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-5 sm:mt-7 pt-4 sm:pt-5 border-t border-white/10 flex-1 flex items-end">
          <div className="w-full flex items-center justify-between">
            <span className="text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 group-hover:text-red-400 transition">
              {p.cta}
            </span>
            <div className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-red-600 group-hover:border-red-600 transition">
              <ArrowUpRight size={15} className="text-white/70 group-hover:text-white transition" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function PortailsProSection() {
  return (
    <section id="portails-pro" className="max-w-7xl mx-auto px-4 lg:px-6 mt-12 sm:mt-20 lg:mt-28" data-testid="portails-pro-section">
      {/* Header */}
      <div className="mb-7 sm:mb-10 lg:mb-14 max-w-4xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4 sm:mb-5">
          <Award size={13} className="text-red-400" />
          <span className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Deux interfaces · Une mission</span>
        </div>
        <h2 className="font-serif-editorial text-white text-[28px] sm:text-[44px] lg:text-[68px] leading-[1.02] sm:leading-[0.98] tracking-tight">
          Réussir le Code.<br />
          <span className="text-red-500">Réussir la conduite.</span>
        </h2>
        <p className="mt-4 sm:mt-6 text-white/65 text-[13.5px] sm:text-[15px] lg:text-[16px] leading-relaxed max-w-2xl">
          Outil pédagogique pensé par des moniteurs ECSR pour aider les jeunes apprenants à réussir l'examen du Code et de la conduite. Élève et enseignant connectés pour un suivi en temps réel et la validation des compétences.
        </p>
      </div>

      {/* 2 portail cards */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-5">
        {PORTAILS.map((p, i) => <PortailCard key={p.role} p={p} index={i} />)}
      </div>

      {/* Bottom strip */}
      <div className="mt-7 sm:mt-10 lg:mt-12 grid md:grid-cols-3 gap-3 sm:gap-4">
        {[
          { icon: BookOpenCheck, label: 'Référentiel REMC officiel', sub: '38 compétences harmonisées' },
          { icon: Layers, label: 'Élève ↔ Enseignant connectés', sub: 'Code prof · suivi en temps réel' },
          { icon: Award, label: '100 % gratuit · pensé ECSR', sub: 'Aucun bourrage de QCM' },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 sm:p-4 flex items-center gap-3 sm:gap-3.5">
              <div className="w-10 h-10 rounded-lg bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0">
                <Icon size={17} />
              </div>
              <div>
                <div className="text-white text-[13px] sm:text-[13.5px] font-semibold leading-tight">{item.label}</div>
                <div className="text-white/45 text-[11px] sm:text-[11.5px] mt-0.5">{item.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
